import services from '../../services'

export interface FotoBaixada { bytes: ArrayBuffer; mime: string }

/** Busca a foto no portal (que busca no host liberado). Falhou: erro com a frase que o relatório mostra no lugar. */
export async function buscarImagemDoPortal(url: string): Promise<FotoBaixada> {
  const r = await services.api.relatorioHtmlImagem(url)
  if (r.fail || !r.data) throw new Error(typeof r.error === 'string' && r.error ? r.error : 'Foto indisponível')
  const mime = String(r.tipo || '').split(';')[0].trim().toLowerCase()
  if (!mime.startsWith('image/')) throw new Error('Foto indisponível')
  return { bytes: r.data as ArrayBuffer, mime }
}

interface Opcoes {
  buscar: (url: string) => Promise<FotoBaixada>
  /** Quantas fotos o portal pede ao servidor ao mesmo tempo. */
  maxJuntos?: number
  /** Quantas fotos ficam guardadas para o relatório redesenhar sem pedir de novo. */
  maxGuardadas?: number
}

/**
 * O que atende o Portal.imagem do relatório: guarda as últimas fotos, junta pedidos iguais e limita quantos vão ao
 * servidor ao mesmo tempo (o relatório pede as fotos conforme aparecem na tela). Cada resposta leva uma cópia dos bytes,
 * porque quem recebe por postMessage com transferência fica sem o original.
 */
export function criarCarregadorDeImagens({ buscar, maxJuntos = 4, maxGuardadas = 80 }: Opcoes) {
  const guardadas = new Map<string, FotoBaixada>()
  const emAndamento = new Map<string, Promise<FotoBaixada>>()
  const espera: Array<() => void> = []
  let ativos = 0

  const vez = () =>
    new Promise<void>(resolver => {
      if (ativos < maxJuntos) {
        ativos++
        resolver()
      } else {
        espera.push(() => {
          ativos++
          resolver()
        })
      }
    })

  const liberar = () => {
    ativos--
    espera.shift()?.()
  }

  const baixar = async (url: string): Promise<FotoBaixada> => {
    await vez()
    try {
      const foto = await buscar(url)
      guardadas.set(url, foto)
      while (guardadas.size > maxGuardadas) guardadas.delete(guardadas.keys().next().value as string)
      return foto
    } finally {
      liberar()
    }
  }

  return async (url: string): Promise<FotoBaixada> => {
    let foto = guardadas.get(url)
    if (foto) {
      // Usada agora: vai para o fim da fila de descarte.
      guardadas.delete(url)
      guardadas.set(url, foto)
    } else {
      let pedido = emAndamento.get(url)
      if (!pedido) {
        pedido = baixar(url).finally(() => emAndamento.delete(url))
        emAndamento.set(url, pedido)
      }
      foto = await pedido
    }
    return { bytes: foto.bytes.slice(0), mime: foto.mime }
  }
}
